import { useRef, useState } from "react";
import { ScanSearch, Upload } from "lucide-react";
import { api } from "../../lib/api";
import { Panel, Result, Cta, field, label } from "./_ui";

const SIZES = [
  { label: "Business card 3.5 × 2", w: 3.5, h: 2 },
  { label: "Postcard 4 × 6", w: 4, h: 6 },
  { label: "Flyer 8.5 × 11", w: 8.5, h: 11 },
  { label: "Poster 18 × 24", w: 18, h: 24 },
  { label: "Yard sign 24 × 36", w: 24, h: 36 },
  { label: "Retractable 33 × 81", w: 33, h: 81 },
  { label: "Banner 4 × 8 ft", w: 48, h: 96 },
  { label: "SEG wall 10 ft", w: 118, h: 89 },
];

function verdict(dpi, large) {
  const min = large ? 100 : 150, good = large ? 150 : 300;
  if (dpi >= good) return { tone: "good", text: "Sharp — press-ready resolution" };
  if (dpi >= min) return { tone: "warn", text: "Acceptable — fine details may soften" };
  return { tone: "bad", text: "Too low — will print soft or pixelated" };
}

export function DpiChecker({ initialW, initialH }) {
  const [w, setW] = useState(Number(initialW) > 0 ? Number(initialW) : 3.5);
  const [h, setH] = useState(Number(initialH) > 0 ? Number(initialH) : 2);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [err, setErr] = useState("");
  const input = useRef(null);

  const run = async (file) => {
    if (!file) return;
    setBusy(true); setErr(""); setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("target_w", String(w)); fd.append("target_h", String(h));
      const { data } = await api.post("/tools/dpi-check", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(data);
    } catch (e) { setErr(e.response?.data?.detail || "Could not read that file."); }
    setBusy(false);
  };

  const preset = (e) => { const p = SIZES[+e.target.value]; if (p) { setW(p.w); setH(p.h); } };
  const large = Math.max(w, h) >= 24;
  const dpi = report?.checks?.effective_dpi ?? (report?.checks?.min_image_dpi ?? null);
  const v = dpi != null ? verdict(dpi, large) : null;
  const px = report?.checks?.pixels;

  return (
    <Panel icon={ScanSearch} title="Image resolution (DPI) checker" testId="dpi-checker">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="col-span-2"><label className={label}>Print size preset</label>
          <select className={field} onChange={preset} defaultValue="" data-testid="dpi-preset"><option value="">Custom…</option>{SIZES.map((s, i) => <option key={s.label} value={i}>{s.label}</option>)}</select></div>
        <div><label className={label}>Print width (in)</label><input type="number" step="0.1" min="0.5" value={w} onChange={(e) => setW(+e.target.value)} className={field} data-testid="dpi-width" /></div>
        <div><label className={label}>Print height (in)</label><input type="number" step="0.1" min="0.5" value={h} onChange={(e) => setH(+e.target.value)} className={field} data-testid="dpi-height" /></div>
      </div>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); run(e.dataTransfer.files?.[0]); }}
        onClick={() => input.current?.click()}
        className="border-2 border-dashed border-white/20 hover:border-[#B87333] rounded-sm p-6 text-center cursor-pointer transition-colors"
        data-testid="dpi-dropzone"
      >
        <Upload className="w-6 h-6 mx-auto text-[#B87333] mb-2" />
        <div className="text-sm">{busy ? "Checking…" : "Drop a JPG, PNG, TIFF or PDF here — or click to choose"}</div>
        <div className="text-[11px] text-white/50 mt-1">Files are inspected and discarded; nothing is stored. Up to 60 MB.</div>
        <input ref={input} type="file" accept=".jpg,.jpeg,.png,.tif,.tiff,.pdf,.webp" className="hidden" onChange={(e) => run(e.target.files?.[0])} data-testid="dpi-file-input" />
      </div>
      {err && <div className="mt-3 text-sm text-red-300" data-testid="dpi-error">{err}</div>}
      {report && (
        <div className="mt-4 space-y-3" data-testid="dpi-report">
          <div className="grid sm:grid-cols-3 gap-3">
            <Result title="Effective resolution" value={dpi != null ? `${dpi} dpi` : "—"} sub={`at ${w}" × ${h}"${large ? " (large format: 100 dpi min, 150 ideal)" : " (150 dpi min, 300 ideal)"}`} testId="dpi-result-dpi" tone={v?.tone} />
            <Result title="Verdict" value={v?.text || "Inspected"} testId="dpi-result-verdict" tone={v?.tone} />
            <Result title="File" value={px ? `${px[0]} × ${px[1]} px` : report.checks?.pages ? `PDF · ${report.checks.pages} page${report.checks.pages > 1 ? "s" : ""}` : report.filename} sub={report.checks?.mode ? `Colour mode ${report.checks.mode}` : report.checks?.page_size_in ? `Page ${report.checks.page_size_in.join(" × ")} in` : null} testId="dpi-result-file" />
          </div>
          {report.flags?.length > 0 && (
            <ul className="text-xs space-y-1" data-testid="dpi-flags">
              {report.flags.map((f, i) => (
                <li key={i} className={f.level === "error" ? "text-red-300" : f.level === "warn" ? "text-amber-300" : "text-white/70"}>• {f.message}</li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <Cta to="/quote?topic=design-assist" testId="dpi-cta-human">Have a Kolt prepress tech check it</Cta>
            <Cta to="/shop" outline testId="dpi-cta-shop">Order this size</Cta>
          </div>
        </div>
      )}
    </Panel>
  );
}
